import React, { createContext, useContext, useState, useRef } from 'react';
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

const AttachmentContext = createContext();

export const useAttachments = () => {
    const context = useContext(AttachmentContext);
    if (!context) {
        throw new Error('useAttachments must be used within AttachmentProvider');
    }
    return context;
};

export const AttachmentProvider = ({ children, task, onTaskChange, descriptionEditorRef, newTask = false }) => {
    const { axiosInstance } = useAuth();
    const { toast } = useToast();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [submissionFileUrls, setSubmissionFileUrls] = useState({});
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const fileInputRef = useRef(null);

    // Generate URLs for submission files so thumbnails can display
    React.useEffect(() => {
        if (newTask && task?.attachments) {
            const generateUrls = async () => {
                const urls = {};
                for (const file of task.attachments) {
                    if (file.fromSubmission && file.key && file.contentType?.startsWith('image/')) {
                        try {
                            const response = await axiosInstance.get(`/forms/files/${file.key}`);
                            urls[file.key] = response.data.url;
                        } catch (error) {
                            console.error('Error getting file URL:', error);
                        }
                    }
                }
                setSubmissionFileUrls(urls);
            };

            generateUrls();
        }
    }, [task?.attachments, newTask, axiosInstance]);

    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;

        console.log('Starting upload of files:', files.map(f => ({
            name: f.name,
            size: f.size,
            type: f.type
        })));

        // For newTask mode, just add the files to the task without uploading
        if (newTask) {
            const tempFiles = files.map((file, index) => {
                const uploadId = Math.random().toString(36).substring(7);
                return {
                    id: uploadId,
                    key: `temp_${Date.now()}_${index}_${Math.random().toString(36).substring(7)}`,
                    name: file.name,
                    contentType: file.type,
                    type: file.type,
                    size: file.size,
                    url: URL.createObjectURL(file),
                    file: file,
                    alt: file.name,
                    uploadId: uploadId,
                    fromSubmission: false
                };
            });
            
            const updatedTask = {
                ...task,
                attachments: [...(task.attachments || []), ...tempFiles]
            };
            
            onTaskChange(updatedTask);
            return;
        }

        // For existing tasks, upload all files
        setIsUploading(true);
        setUploadProgress(0);

        const succeeded = [];
        const failed = [];

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                try {
                    const formData = new FormData();
                    formData.append('file', file);

                    const response = await axiosInstance.post(
                        `/tasks/${task.organization.slug}/${task.key}/attachments`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                            onUploadProgress: (progressEvent) => {
                                const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                const totalProgress = Math.round(((i * 100) + fileProgress) / files.length);
                                setUploadProgress(totalProgress);
                            },
                            timeout: 30000
                        }
                    );

                    onTaskChange(response.data);
                    succeeded.push(file);
                } catch (uploadErr) {
                    console.error(`Failed to upload "${file.name}":`, uploadErr);
                    failed.push(file);
                }
            }

            if (failed.length > 0 && succeeded.length > 0) {
                toast({
                    description: (
                        <Collapsible>
                            <CollapsibleTrigger className="flex items-center gap-1 font-medium cursor-pointer">
                                {succeeded.length} uploaded, {failed.length} failed
                                <ChevronDown className="h-3 w-3" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-1 text-xs opacity-80">
                                {failed.map((f, i) => <div key={i}>{f.name}</div>)}
                            </CollapsibleContent>
                        </Collapsible>
                    ),
                });
            } else if (failed.length > 0) {
                toast({
                    description: (
                        <Collapsible>
                            <CollapsibleTrigger className="flex items-center gap-1 font-medium cursor-pointer">
                                Failed to upload {failed.length} file{failed.length > 1 ? 's' : ''}
                                <ChevronDown className="h-3 w-3" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-1 text-xs opacity-80">
                                {failed.map((f, i) => <div key={i}>{f.name}</div>)}
                            </CollapsibleContent>
                        </Collapsible>
                    ),
                });
            } else {
                toast({
                    description: `${files.length} attachment${files.length > 1 ? 's' : ''} uploaded successfully`,
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast({
                description: "Failed to upload files",
                variant: "destructive"
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteAttachment = async (attachment) => {
        if (newTask) {
            // For new tasks, remove from the array and also from the editor
            const updatedAttachments = task.attachments.filter(att => att !== attachment);
            
            // Remove the attachment from the editor content if it exists
            if (descriptionEditorRef?.current) {
                const editor = descriptionEditorRef.current;
                const { state } = editor;
                const { doc } = state;
                
                // Collect nodes to delete in reverse order to avoid position issues
                const nodesToDelete = [];
                
                doc.descendants((node, pos) => {
                    if (node.type.name === 's3Image' && node.attrs) {
                        // For images, check if the key, alt, or filename matches
                        if (node.attrs.key === attachment.key || 
                            node.attrs.alt === attachment.name || 
                            node.attrs.alt === attachment.filename) {
                            nodesToDelete.push({ pos, size: node.nodeSize });
                        }
                    } else if (node.type.name === 's3File' && node.attrs) {
                        // For files, check if the key or filename matches
                        if (node.attrs.key === attachment.key || 
                            node.attrs.filename === attachment.name || 
                            node.attrs.filename === attachment.filename) {
                            nodesToDelete.push({ pos, size: node.nodeSize });
                        }
                    }
                });
                
                // Delete nodes in reverse order to maintain correct positions
                if (nodesToDelete.length > 0) {
                    const tr = state.tr;
                    nodesToDelete.reverse().forEach(({ pos, size }) => {
                        tr.delete(pos, pos + size);
                    });
                    editor.view.dispatch(tr);
                }
            }
            
            onTaskChange({ ...task, attachments: updatedAttachments });
            setAttachmentToDelete(null);
            setDeleteModalOpen(false);
            return;
        }

        // For existing tasks, call the API
        setIsDeleting(true);
        try {
            const response = await axiosInstance.delete(
                `/tasks/${task.organization.slug}/${task.key}/attachments`,
                { data: { key: attachment.key } }
            );

            if (descriptionEditorRef?.current) {
                descriptionEditorRef.current.commands.setContent(response.data.description);
            }

            onTaskChange(response.data);
            setDeleteModalOpen(false);
            setAttachmentToDelete(null);
            toast({
                description: "Attachment deleted successfully"
            });
        } catch (error) {
            console.error('Error deleting attachment:', error);
            toast({
                description: error?.response?.data?.message || "Failed to delete attachment",
                variant: "destructive"
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenAttachment = (attachment) => {
        if (attachment.contentType?.startsWith('image/')) {
            // For images, open in lightbox
            const url = attachment.fromSubmission && submissionFileUrls[attachment.key] 
                ? submissionFileUrls[attachment.key] 
                : attachment.url;
            
            setLightboxImage({ 
                url, 
                alt: attachment.filename || attachment.name,
                attachment
            });
            setLightboxOpen(true);
        } else {
            // For non-images, open in new tab
            const url = attachment.fromSubmission && submissionFileUrls[attachment.key] 
                ? submissionFileUrls[attachment.key] 
                : attachment.url;
            window.open(url, '_blank');
        }
    };

    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    const value = {
        // State
        task,
        newTask,
        isUploading,
        uploadProgress,
        attachmentToDelete,
        deleteModalOpen,
        isDeleting,
        submissionFileUrls,
        lightboxImage,
        lightboxOpen,
        fileInputRef,
        
        // Actions
        handleFileUpload,
        handleDeleteAttachment,
        handleOpenAttachment,
        triggerFileUpload,
        setAttachmentToDelete,
        setDeleteModalOpen,
        setLightboxImage,
        setLightboxOpen,
        
        // Helpers
        getAttachmentUrl: (attachment) => {
            if (attachment.fromSubmission && submissionFileUrls[attachment.key]) {
                return submissionFileUrls[attachment.key];
            }
            return attachment.url;
        }
    };

    return (
        <AttachmentContext.Provider value={value}>
            {children}
            <ImageLightbox />
        </AttachmentContext.Provider>
    );
}; 